import AddRounded from '@mui/icons-material/AddRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import MarkunreadMailboxRounded from '@mui/icons-material/MarkunreadMailboxRounded';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, FormikHelpers, FormikProvider, getIn, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';

import { useUpdatePiecesMutation } from '@api/piece';
import { useCreateUserAddressMutation } from '@api/user';
import { PieceAction } from '@apiContract';
import { FormAddressFields } from '@components/FormAddressFields';
import { addressValidationSchema } from '@components/FormAddressFields/config';
import { AddressId, IFormAddressFields } from '@components/FormAddressFields/types';
import _ from 'lodash';
import { toast } from 'sonner';
import NotifyMailroomSwitch from 'src/components/NotifyMailroomSwitch';

import { ActionComponentProps } from '../../../index';
import { CollapsibleOptions } from '../CollapsibleOptions';
import { buildUpdatePiecesRequest } from '../helper';
import { AddressItem } from './components/AddressItem';
import { useMe } from '@hooks/useMe';

interface MailToMeFormikValues extends IFormAddressFields {
  default: boolean;
}

// TODO: Refactor inner form submit logic.
export const RequestMailToMe = ({ mailpieces, actionStarted, onActionFinish, isForce }: ActionComponentProps) => {
  const { currentUser } = useMe();
  const [updatePieces] = useUpdatePiecesMutation();
  const [createUserAddress] = useCreateUserAddressMutation();

  const addresses = useMemo(() => currentUser?.addresses || [], [currentUser]);
  const defaultAddress = useMemo(() => addresses?.find((address) => address.default), [addresses]);

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [selectedAddressId, setSelectedAddressId] = useState<AddressId | null>(null);
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [shouldNotifyMailroom, setShouldNotifyMailroom] = useState(false);

  const pieceQuantity = mailpieces.length;

  const formik = useFormik<MailToMeFormikValues>({
    initialValues: {
      description: '',
      addressLine1: '',
      addressLine2: '',
      countryCode: 'US',
      postalCode: '',
      locality: '',
      administrativeArea: '',
      default: false,
    },
    validationSchema: addressValidationSchema,
    onSubmit: async (values: MailToMeFormikValues, { resetForm }: FormikHelpers<MailToMeFormikValues>) => {
      try {
        if (!currentUser) return;

        setActionInProgress(true);

        const newAddressList = await createUserAddress({ userId: currentUser.id, ...values }).unwrap();
        const createdAddress = _.differenceWith(newAddressList, addresses, _.isEqual)[0];

        const requestData = buildUpdatePiecesRequest({
          mailpieces,
          action: PieceAction.RequestMail,
          isForce,
          shouldNotifyMailroom,
          additionalData: {
            addressId: createdAddress.id,
          },
        });

        await updatePieces(requestData).unwrap();

        resetForm();
        onActionFinish();
      } catch (error) {
        console.error(error);
      } finally {
        setActionInProgress(false);
      }
    },
  });

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm } = formik;

  useEffect(() => {
    if (actionStarted) {
      resetForm();
      setOpenCreateForm(false);
      setSelectedAddressId(defaultAddress?.id || null);
      setShouldNotifyMailroom(false);
      if (!addresses.length) {
        setOpenCreateForm(true);
      }
    }
  }, [actionStarted, defaultAddress, addresses, resetForm]);

  useEffect(() => {
    if (openCreateForm && !addresses.length) {
      setFieldValue('default', true);
    }
  }, [openCreateForm, addresses, setFieldValue]);

  const handleToggleCreateForm = () => {
    if (openCreateForm) {
      setSelectedAddressId(defaultAddress?.id || null);
    } else {
      setSelectedAddressId(null);
    }
    setOpenCreateForm((value) => !value);
  };

  const handleSelectAddress = (addressId: AddressId) => {
    setSelectedAddressId(addressId);
    setOpenCreateForm(false);
  };

  const handleToggleIsDefault = () => setFieldValue('default', !values.default);

  const onSubmitSelectedAddress = async () => {
    if (!selectedAddressId) return;

    try {
      setActionInProgress(true);

      const requestData = buildUpdatePiecesRequest({
        mailpieces,
        action: PieceAction.RequestMail,
        isForce,
        shouldNotifyMailroom,
        additionalData: {
          addressId: selectedAddressId,
        },
      });

      await updatePieces(requestData).unwrap();
      onActionFinish();
    } catch (error) {
      console.error(error);
    } finally {
      setActionInProgress(false);
    }
  };

  const submit = () => {
    if (!selectedAddressId && !openCreateForm) return toast.error('Please, select an address', { duration: 3000 });
    const fn = selectedAddressId ? onSubmitSelectedAddress : handleSubmit;
    fn();
  };

  return (
    <Dialog maxWidth="sm" open={actionStarted} onClose={onActionFinish}>
      <FormikProvider value={formik}>
        <Form>
          <DialogTitle sx={{ py: 2.25, px: 3 }}>{pieceQuantity > 1 ? 'Request mailing for selected items' : 'Request mailing'}</DialogTitle>
          <DialogContent>
            <Stack direction="column" spacing={3}>
              <Typography sx={{ color: 'text.secondary' }} variant="body1">
                {pieceQuantity > 1 ? (
                  <>
                    Select the address you would like <strong>{pieceQuantity}</strong> items mailed to.
                  </>
                ) : (
                  'Select the address you would like this item mailed to.'
                )}
              </Typography>
              {addresses.map((address) => (
                <AddressItem
                  key={address.id}
                  address={address}
                  isSelected={selectedAddressId === address.id}
                  onSelect={handleSelectAddress}
                />
              ))}
              {!addresses.length && (
                <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                  You don't have addresses yet.
                </Typography>
              )}
              <Box sx={{ display: 'flex' }}>
                <Button
                  size="small"
                  onClick={handleToggleCreateForm}
                  sx={{ color: 'primary.main' }}
                  startIcon={<AddRounded />}
                >
                  Add New Address
                </Button>
              </Box>
              <Collapse in={openCreateForm}>
                <Paper
                  variant="outlined"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    transition: (theme) => theme.transitions.create('all'),
                    ...(openCreateForm && {
                      boxShadow: (theme) => theme.customShadows.z12,
                    }),
                    p: 3,
                    gap: 3,
                  }}
                >
                  <FormControlLabel
                    control={<Radio checked={true} checkedIcon={<CheckCircleRounded />} />}
                    label={<Typography variant="subtitle1">Add New Address</Typography>}
                    disableTypography
                  />
                  <TextField
                    label="Address Name"
                    name="description"
                    fullWidth
                    required
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    variant="outlined"
                    error={Boolean(getIn(touched, 'description') && !!getIn(errors, 'description'))}
                    helperText={getIn(touched, 'description') && getIn(errors, 'description')}
                  />
                  <FormAddressFields />
                  <Box>
                    <FormControlLabel
                      label="Use address as default"
                      control={
                        <Checkbox
                          disabled={addresses.length === 0}
                          checked={values.default}
                          onChange={handleToggleIsDefault}
                        />
                      }
                    />
                  </Box>
                </Paper>
              </Collapse>
            </Stack>
            {isForce && (
              <CollapsibleOptions disableTopMargin>
                <Box sx={{ pt: 1 }}>
                  <NotifyMailroomSwitch
                    checked={shouldNotifyMailroom}
                    onChange={() => setShouldNotifyMailroom(!shouldNotifyMailroom)}
                  />
                </Box>
              </CollapsibleOptions>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onActionFinish} color="inherit" disabled={actionInProgress}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              loading={actionInProgress}
              startIcon={actionInProgress && <SaveIcon />}
              loadingPosition={actionInProgress ? 'start' : undefined}
              variant="contained"
              color="primary"
            >
              Submit
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
};

export const requestMailToMeConfig = {
  title: 'Mail',
  Icon: MarkunreadMailboxRounded,
};
